import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@evasys/globals/evasys/environments/environment';
import { firstValueFrom, map, tap } from 'rxjs';
import { TranslocoService } from '@ngneat/transloco';
import { AuthUserService, ConfigService } from '@evasys/shared/core';
import { EvasysLoadingStrategiesEnum } from '@evasys/globals/shared/enums/general/evasys-loadingStrategies.enum';
import { contrast, hexToRgb, isLight, mix, shade, tint } from '@evasys/globals/shared/helper/color';
import { Dictionary } from '@ngrx/entity';
import { SystemInfoService } from '@evasys/evasys/shared/core';

export function initDefaultLanguage(
	applicationInit: ApplicationInit,
	httpClient: HttpClient,
	translocoService: TranslocoService,
	authUserService: AuthUserService
) {
	return (): Promise<any> => {
		return applicationInit.initDefaultLanguage(httpClient, translocoService, authUserService);
	};
}

export function initColorSettings(applicationInit: ApplicationInit, document: Document, configService: ConfigService) {
	return (): Promise<void> => {
		return applicationInit.initColorSettings(document, configService);
	};
}

export function initTitle(applicationInit: ApplicationInit, document: Document, systemInfoService: SystemInfoService) {
	return () => applicationInit.initTitle(document, systemInfoService);
}

@Injectable()
export class ApplicationInit {
	public initDefaultLanguage(
		httpClient: HttpClient,
		translocoService: TranslocoService,
		authUserService: AuthUserService
	) {
		return authUserService.authenticatedUserPromise
			.then((user) => {
				if (user && user.id) {
					return firstValueFrom(authUserService.authenticateUser(user?.id?.toString()));
				} else {
					return user;
				}
			})
			.then((user) => {
				if (user && user.lang && user.flavor) {
					const lang = user.lang + '_' + user.flavor;
					translocoService.setDefaultLang(lang);
					translocoService.setActiveLang(lang);
				} else {
					return firstValueFrom(
						httpClient.get(environment.apiHref + 'configs?filter=name eq DEFAULT_LANGUAGE').pipe(
							map(
								(configResponse: any) =>
									configResponse.results[0].value ?? configResponse.results[0].oldValue
							),
							tap((lang) => {
								translocoService.setDefaultLang(lang);
								translocoService.setActiveLang(lang);
							})
						)
					);
				}
			});
	}

	public initColorSettings(document: Document, configService: ConfigService): Promise<void> {
		const self = ApplicationInit;

		return firstValueFrom(
			configService
				.getManyConfigs(
					[
						'bgcolor1',
						'bgcolor2',
						'bgcolor3',
						'bgcolor4',
						'bgcolor5',
						'bgcolor6',
						'bgcolor7',
						'bgcolor8',
						'bgcolor9',
						'bgcolor10',
						'tabcolor1',
						'tabcolor2',
						'tabcolor3',
						'tabcolor4',
						'tabcolor5',
						'color1',
						'color2',
						'color3',
						'color4',
						'color5',
						'color6',
						'color_primary',
						'color_secondary',
						'color_light',
						'color_table_border',
						'color_card_background',
						'color_card_font',
					],
					EvasysLoadingStrategiesEnum.APIONLY
				)
				.pipe(map((colors) => self.buildColorMap(colors)))
		).then((colorMap) => {
			let colorSettingStyles = document.querySelector('#colorSettingStyles');
			if (!colorSettingStyles) {
				colorSettingStyles = document.createElement('style');
				colorSettingStyles.id = 'colorSettingStyles';

				document.querySelector('head').appendChild(colorSettingStyles);
			}

			let colorSettingSheet = ':root{';

			colorMap.forEach((color, varName) => {
				if (color) {
					colorSettingSheet += `--${varName}:${color};`;
				}
			});

			colorSettingSheet += '}';

			colorSettingStyles.innerHTML = colorSettingSheet;
		});
	}

	private static buildColorMap(colors: Dictionary<string>): Map<string, string> {
		const self = ApplicationInit;

		const colorMap = new Map<string, string>();

		const topmenuBg = colors['bgcolor9'];
		colorMap.set('eva-topmenu-bg', topmenuBg);
		colorMap.set('eva-topmenu-logo-bg', colors['bgcolor10']);
		colorMap.set('eva-topmenu-item-color', colors['color6']);
		colorMap.set('eva-topmenu-item-active-color', colors['bgcolor5']);

		colorMap.set('eva-sidebar-bg', colors['bgcolor1']);
		colorMap.set('eva-sidemenu-item-color', colors['bgcolor8']);
		colorMap.set('eva-sidemenu-item-hover-color', colors['bgcolor4']);
		colorMap.set('eva-sidemenu-heading-bg', colors['bgcolor3']);
		colorMap.set('eva-sidemenu-heading-color', colors['color1']);
		colorMap.set('eva-sidemenu-item-active-bg', colors['bgcolor7']);
		colorMap.set('eva-sidemenu-item-active-color', colors['bgcolor6']);

		self.addColorWithRgb(colorMap, 'bs-body-bg', colors['bgcolor2']);
		self.addColorWithRgb(colorMap, 'bs-body-color', colors['color2']);
		const primary = colors['color_primary'];
		self.addColorWithRgb(colorMap, 'bs-primary', primary);
		const secondary = colors['color_secondary'];
		self.addColorWithRgb(colorMap, 'bs-secondary', secondary);
		const light = colors['color_light'];
		self.addColorWithRgb(colorMap, 'bs-light', light);

		colorMap.set('eva-link-color', colors['color3']);
		colorMap.set('eva-link-hover-color', colors['color5']);
		colorMap.set('eva-hint-color', colors['color4']);

		colorMap.set('eva-table-row-bg-odd', colors['tabcolor1']);
		colorMap.set('eva-table-row-bg-even', colors['tabcolor2']);
		colorMap.set('eva-table-header-bg', colors['tabcolor3']);
		colorMap.set('eva-table-header-color', colors['tabcolor4']);
		colorMap.set('eva-table-row-color', colors['tabcolor5']);
		colorMap.set('eva-table-border-color', colors['color_table_border']);

		colorMap.set('eva-card-bg', colors['color_card_background']);
		colorMap.set('eva-card-color', colors['color_card_font']);

		// region grays

		const gray2 = '#fafafa';
		const gray20 = '#cccccc';
		const gray40 = '#9a9a9a';
		const gray70 = '#4c4c4c';
		const gray75 = '#3e3e3e';
		const gray80 = '#333333';
		const gray85 = '#222222';
		const gray90 = '#191919';
		const black = '#000000';

		const lightTheme = !topmenuBg || isLight(topmenuBg);

		const usedGray2 = lightTheme ? gray2 : gray20;
		colorMap.set('eva-gray2', usedGray2);
		const usedGray20 = gray20;
		colorMap.set('eva-gray20', usedGray20);
		const usedGray40 = lightTheme ? gray20 : gray40;
		colorMap.set('eva-gray40', usedGray40);
		const usedGray70 = gray70;
		colorMap.set('eva-gray70', usedGray70);
		const usedGray75 = lightTheme ? gray70 : gray75;
		colorMap.set('eva-gray75', usedGray75);
		const usedGray80 = gray80;
		colorMap.set('eva-gray80', usedGray80);
		const usedGray85 = lightTheme ? gray80 : gray85;
		colorMap.set('eva-gray85', usedGray85);
		const usedGray90 = lightTheme ? gray80 : gray90;
		colorMap.set('eva-gray90', usedGray90);

		self.addColorWithRgb(colorMap, 'bs-black', lightTheme ? black : gray90);

		// region Bootstrap grays

		colorMap.set('bs-gray-100', usedGray2);
		colorMap.set('bs-gray-200', usedGray20);
		colorMap.set('bs-gray-300', usedGray20);
		colorMap.set('bs-gray-400', usedGray40);
		colorMap.set('bs-gray-500', usedGray70);

		colorMap.set('bs-gray-600', usedGray75);
		colorMap.set('bs-gray', usedGray75);

		colorMap.set('bs-gray-700', usedGray80);

		colorMap.set('bs-gray-800', usedGray85);
		colorMap.set('bs-gray-dark', usedGray85);

		colorMap.set('bs-gray-900', usedGray90);
		self.addColorWithRgb(colorMap, 'bs-dark', usedGray90);

		// endregion Bootstrap grays

		// endregion grays

		// region generated colors
		const buttons: Dictionary<string> = {};
		if (primary) {
			colorMap.set('eva-primary-contrast-color', contrast(primary));
			buttons.primary = primary;
		}
		if (secondary) {
			colorMap.set('eva-secondary-contrast-color', contrast(secondary));
			buttons.secondary = secondary;
		}
		if (light) {
			colorMap.set('eva-light-contrast-color', contrast(light));
		}

		// region specialized

		colorMap.set('eva-dropdown-link-hover-color', mix(black, usedGray90, 0.1));

		self.generateButtonColors(colorMap, buttons);

		// endregion specialized

		// endregion generated colors

		return colorMap;
	}

	private static generateButtonColors(colorMap: Map<string, string>, buttons: Dictionary<string>): void {
		for (const [name, value] of Object.entries(buttons)) {
			const shouldTint = isLight(value);

			const hoverBg = shouldTint ? tint(value, 0.15) : shade(value, 0.15);
			colorMap.set('eva-btn-hover-bg-' + name, hoverBg);
			colorMap.set('eva-btn-hover-border-' + name, shouldTint ? tint(value, 0.1) : shade(value, 0.2));
			colorMap.set('eva-btn-hover-bg-' + name + '-contrast', contrast(hoverBg));

			const activeBg = shouldTint ? tint(value, 0.2) : shade(value, 0.2);
			colorMap.set('eva-btn-active-bg-' + name, activeBg);
			colorMap.set('eva-btn-active-border-' + name, shouldTint ? tint(value, 0.1) : shade(value, 0.25));
			colorMap.set('eva-btn-active-bg-' + name + '-contrast', contrast(activeBg));
		}
	}

	private static addColorWithRgb(colorMap: Map<string, string>, name: string, color: string): void {
		if (color) {
			colorMap.set(name, color);
			colorMap.set(name + '-rgb', hexToRgb(color).join());
		}
	}

	public initTitle(document: Document, systemInfoService: SystemInfoService): void {
		firstValueFrom(systemInfoService.getOne('system_type', EvasysLoadingStrategiesEnum.APIONLY))
			.then((systemInfo) => (document.querySelector('title').textContent = systemInfo?.value))
			.catch((err) => console.error('Can not init the title: ', err));
	}
}
